import { Spinner } from '@components/ui/Spinner';
import { BUDGETS, DELIVERY_TIMES } from '@/types';
import type { EnumsResponse } from '@/types';
import type { FormData } from './schema';
import { Button } from '@/components/ui';
import { Separator } from '@/components/ui/Separator';

interface Step3Props {
  values: FormData;
  objectives: string[];
  constraints: string[];
  enums: EnumsResponse | undefined;
  userName: string;
  isSubmitting: boolean;
  readOnly?: boolean;
  onBack: () => void;
  onSubmit: () => void;
  onEdit: () => void;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function Step3({
  values,
  objectives,
  constraints,
  enums,
  userName,
  isSubmitting,
  readOnly,
  onBack,
  onSubmit,
  onEdit,
}: Step3Props) {
  const budgetLabel =
    enums?.budgets[values.budget] ?? BUDGETS[values.budget] ?? '—';
  const deliveryTimeLabel =
    enums?.deliveryTimes[values.deliveryTime] ??
    DELIVERY_TIMES[values.deliveryTime] ??
    '—';

  const deliveryDisplay =
    values.deliveryTime === 3 && values.deliveryDate
      ? `Specific date: ${formatDate(values.deliveryDate)}`
      : deliveryTimeLabel;

  const nonEmptyObjectives = objectives.filter((o) => o.trim() !== '');
  const nonEmptyConstraints = constraints.filter((c) => c.trim() !== '');

  const today = new Date().toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  // Initials for avatar
  const initials = userName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <>
      <div className="mb-8 text-[--text-dark-primary]">
        <h1 className="text-3xl font-bold">
          Ready to share this with our team?
        </h1>
        <p className="mt-[--spacing-5] text-base">
          Once submitted, an assigned coordinator will review your project and
          quote the planning.
        </p>
      </div>

      {/* Review card */}
      <div className="createProjectForm space-y-[--spacing-10] overflow-hidden p-[--spacing-10] pb-[--spacing-12]">
        {/* Top section */}
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="text-3xl font-semibold text-[--text-dark-primary]">
              {values.title}
            </h2>
            {values.summary && (
              <p className="mt-[--spacing-3] text-base text-[--text-dark-secondary]">
                {values.summary}
              </p>
            )}
          </div>
          <Button
            type="button"
            disabled={readOnly || isSubmitting}
            onClick={onEdit}
            variant="secondary"
            className="aspect-square w-[44px]"
            title="Edit"
          >
            <i className="ri-edit-line text-[24px]" />
          </Button>
        </div>

        {/* Client info */}
        <div className="flex items-center gap-[--spacing-8]">
          <div className="flex aspect-square h-[44px] items-center justify-center overflow-hidden rounded-full bg-[--state-brand-active] text-base font-bold text-[#141414]">
            {initials}
            {/* TO DO: Add profile pictures */}
          </div>
          <div className="text-base">
            <p className="font-medium text-[--text-dark-primary]">{userName}</p>
            <p className="font-normal text-[--text-dark-tertiary]">{today}</p>
          </div>
        </div>

        {/* Link */}
        {values.url && (
          <div className="flex items-center gap-[--spacing-8] text-base text-[--text-dark-primary]">
            <i className="ri-global-fill text-[24px] text-[--base-fill-5]" />
            <span>{values.url}</span>
          </div>
        )}

        {/* Divider */}
        <Separator className="w-[120%] -translate-x-[8%]" />

        {/* Project Description */}
        <div className="summaryInfo">
          <h3>Project Description</h3>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#f5f5f5]">
            {values.description}
          </p>
        </div>

        {/* Key Objectives */}
        {nonEmptyObjectives.length > 0 && (
          <div className="summaryInfo">
            <h3>Key Objectives</h3>
            <ol className="list-inside list-decimal space-y-1 text-sm text-[#f5f5f5]">
              {nonEmptyObjectives.map((obj, i) => (
                <li key={i}>{obj}</li>
              ))}
            </ol>
          </div>
        )}

        {/* Constraints */}
        {nonEmptyConstraints.length > 0 && (
          <div className="summaryInfo">
            <h3>Technical Constraints</h3>
            <ul className="list-inside list-disc space-y-1 text-sm text-[#f5f5f5]">
              {nonEmptyConstraints.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Budget + Delivery time */}
        <div className="grid grid-cols-1 gap-[--spacing-5] lg:grid-cols-2">
          <div className="summaryInfo">
            <h3>Indicative budget (USD)</h3>
            <p>{budgetLabel}</p>
          </div>
          <div className="summaryInfo">
            <h3>Delivery time</h3>
            <p>{deliveryDisplay}</p>
          </div>
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="mt-[--spacing-14] flex items-center justify-between">
        <Button
          type="button"
          variant="secondary"
          disabled={readOnly || isSubmitting}
          onClick={onBack}
        >
          <i className="ri-arrow-left-line" />
          Back
        </Button>
        <Button type="button" disabled={isSubmitting} onClick={onSubmit}>
          {isSubmitting ? (
            <>
              <Spinner size="sm" />
              Submitting...
            </>
          ) : (
            'Submit Project'
          )}
        </Button>
      </div>
    </>
  );
}
